import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDisplayTheme } from 'hooks/useUiState'
import { Theme } from 'features/ui/types'
import { TableSortOption, MessageConst } from 'enums'
import messageUtil from 'utils/messageUtil'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { CountTypeStrings } from 'enums/CountType'
import { getExplanationConfigs } from 'components/visualization/Table/HeaderRenderer'
import { AvatarWithBadge, NameCell } from 'components/visualization/AuthorInfo'
import StreamSymbol from 'enums/StreamSymbol'
import { compactNumberFormatter } from 'utils/numberFormatterUtil'
import TableChart from '../../../../visualization/Table'
import { getNumberFormatter, handleEngagementsDisplay } from './utils'

const Table = ({
  rowData,
  onMentionsHandle,
  countType,
  onRowClick
}: {
  rowData: InsightData[]
  onMentionsHandle: (mentions: number | undefined) => string
  countType: CountTypeStrings
  onRowClick?: (targetRow: InsightData) => void
}) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()

  const numberFormatter = getNumberFormatter()

  return (
    <>
      <TableChart
        onRowClick={onRowClick}
        rowData={rowData}
        colDefs={[
          {
            field: 'authorName',
            headerText: t('name'),
            cellProps: {
              textAlign: 'left',
              displayTooltip: false,
              width: '25%'
            },
            customCellRender: (_, { row }) => (
              <>
                <AvatarWithBadge theme={theme} row={row} />
                <NameCell row={row} theme={theme} />
              </>
            ),
            defaultOrder: TableSortOption.DESCEND
          },
          {
            field: 'mentions',
            headerText: messageUtil.countType(countType!),
            headerProps: {
              textAlign: 'right'
            },
            cellProps: {
              width: '25%'
            },
            customCellRender: (cellValue) => <span>{onMentionsHandle(cellValue)}</span>
          },
          {
            field: 'followers',
            headerText: t('followersVisitors'),
            headerProps: {
              textAlign: 'right',
              icon: getExplanationConfigs(t('followerCountExplanation'))
            },
            cellProps: {
              width: '25%'
            },
            customCellRender: (cellValue, { row }) => {
              const hasCellValue = cellValue !== undefined && cellValue !== null
              const isPodcast = row.dataStreams?.[0] === StreamSymbol.PODCAST

              if (hasCellValue) {
                return isPodcast
                  ? compactNumberFormatter({ value: cellValue, precise: false })
                  : numberFormatter.format(cellValue)
              }
              return MessageConst.EM_DASH
            }
          },
          {
            field: 'engagements',
            headerText: t('engagement_other'),
            headerProps: {
              textAlign: 'right',
              icon: getExplanationConfigs(t('engagementMetricsExplanation'))
            },
            cellProps: {
              width: '25%'
            },
            customCellRender: (_, { row }) => <span>{handleEngagementsDisplay(row)}</span>
          }
        ]}
        isDisplayFoot={false}
      />
    </>
  )
}

export default Table
